import React, { useState } from 'react';
import { useCommunity } from '../context/CommunityContext';

const REACTION_TYPES = [
  { key: 'helpful',   icon: '👍', label: 'Hilfreich',  color: '#059669', activeBg: '#d1fae5' },
  { key: 'insightful',icon: '💡', label: 'Lehrreich',  color: '#d97706', activeBg: '#fef3c7' },
  { key: 'debatable', icon: '💬', label: 'Diskutabel', color: '#3b82f6', activeBg: '#dbeafe' },
];

const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
function avatarColor(initials) {
  if (!initials || initials === 'Du') return '#0ea5e9';
  const code = initials.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function CommunityPage() {
  const {
    getMyHooks,
    removeHook,
    getMostReadHooks,
    getRecentHooks,
    incrementViews,
    MOCK_COMMUNITY_HOOKS,
    getDiscussions,
    addComment,
    toggleReaction,
    userReactions,
  } = useCommunity();

  const [activeTab, setActiveTab] = useState('community');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [commentText, setCommentText] = useState('');

  const formatDate = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const formatDateShort = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  };

  const getHooksForTab = () => {
    if (activeTab === 'mine') {
      return getMyHooks().map((h) => ({ ...h, author: 'Du', isMock: false }));
    }
    return MOCK_COMMUNITY_HOOKS;
  };

  const matchesSearch = (hook) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    if (hook.name && hook.name.toLowerCase().includes(q)) return true;
    if (hook.author && hook.author.toLowerCase().includes(q)) return true;
    if (hook.summary && Array.isArray(hook.summary)) {
      for (const section of hook.summary) {
        if (section.title && section.title.toLowerCase().includes(q)) return true;
        for (const item of section.items || []) {
          if (item.toLowerCase().includes(q)) return true;
        }
      }
    }
    if (hook.diagnoses && Array.isArray(hook.diagnoses)) {
      for (const d of hook.diagnoses) {
        if (d.name && d.name.toLowerCase().includes(q)) return true;
      }
    }
    return false;
  };

  const filteredHooks = getHooksForTab().filter(matchesSearch);
  const recentHooks = getRecentHooks().slice(0, 5);
  const mostReadHooks = getMostReadHooks().slice(0, 5);

  const getPreview = (hook) => {
    if (hook.summary && Array.isArray(hook.summary) && hook.summary.length > 0) {
      const items = hook.summary[0].items || [];
      return items.slice(0, 3).join(' \u2022 ');
    }
    return 'Keine Vorschau verfügbar';
  };

  const getDiagnosesCount = (hook) => {
    if (hook.diagnoses && Array.isArray(hook.diagnoses)) return hook.diagnoses.length;
    return 0;
  };

  const handleExpand = (hook) => {
    const newId = expandedId === hook.id ? null : hook.id;
    setExpandedId(newId);
    if (newId && hook.isMock) {
      incrementViews(hook.id);
    }
  };

  const handleWidgetClick = (hook) => {
    if (hook.isMock) {
      setActiveTab('community');
    } else {
      setActiveTab('mine');
    }
    setSearchQuery('');
    setExpandedId(hook.id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      removeHook(deleteConfirmId);
      if (expandedId === deleteConfirmId) setExpandedId(null);
      setDeleteConfirmId(null);
    }
  };

  return (
    <div className="page page-wide">
      <h1 className="page-title">Community</h1>
      <p className="page-subtitle">Teile und entdecke medizinische Fallbeispiele</p>

      <div className="community-layout">
        <div className="community-main">
          <div className="community-search-wrapper">
            <svg className="community-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              className="community-search"
              type="text"
              placeholder="Beiträge durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="community-tabs">
            <button
              className={`community-tab${activeTab === 'community' ? ' active' : ''}`}
              onClick={() => { setActiveTab('community'); setExpandedId(null); }}
            >
              Community
            </button>
            <button
              className={`community-tab${activeTab === 'mine' ? ' active' : ''}`}
              onClick={() => { setActiveTab('mine'); setExpandedId(null); }}
            >
              Meine Beiträge
            </button>
          </div>

          {filteredHooks.length === 0 ? (
            <div className="community-empty">
              {activeTab === 'mine'
                ? 'Du hast noch keine Fallbeiträge geteilt. Teile Konsultationen aus dem Archiv mit der Community.'
                : 'Keine Beiträge gefunden.'}
            </div>
          ) : (
            <div className="community-hook-list">
              {filteredHooks.map((hook) => (
                <div
                  key={hook.id}
                  className={`community-hook-card${expandedId === hook.id ? ' expanded' : ''}`}
                  onClick={() => handleExpand(hook)}
                >
                  <div className="community-hook-header">
                    <div className="community-hook-header-left">
                      <h3 className="community-hook-name">{hook.name}</h3>
                      <div className="community-hook-meta">
                        <span className="community-hook-author">{hook.author}</span>
                        <span className="community-hook-date">{formatDate(hook.sharedAt)}</span>
                        {getDiagnosesCount(hook) > 0 && (
                          <span className="community-hook-diagnoses-count">
                            {getDiagnosesCount(hook)} Diagnose{getDiagnosesCount(hook) !== 1 ? 'n' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    {hook.views > 0 && (
                      <span className="community-hook-views">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        {hook.views}
                      </span>
                    )}
                  </div>
                  <p className="community-hook-preview">{getPreview(hook)}</p>

                  {expandedId === hook.id && (
                    <div className="community-hook-detail">
                      {hook.summary && Array.isArray(hook.summary) && hook.summary.length > 0 && (
                        <div className="session-detail-section">
                          <h4>Zusammenfassung</h4>
                          {hook.summary.map((section, i) => (
                            <div key={i} className="session-detail-summary-section">
                              <strong>{section.title}</strong>
                              <ul>
                                {(section.items || []).map((item, j) => (
                                  <li key={j}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}

                      {hook.diagnoses && Array.isArray(hook.diagnoses) && hook.diagnoses.length > 0 && (
                        <div className="session-detail-section">
                          <h4>Differentialdiagnosen</h4>
                          {hook.diagnoses.map((d, i) => (
                            <div key={i} className="session-detail-diagnosis">
                              <strong>{d.name}</strong>
                              <p>{d.reason}</p>
                              {d.diagnostics && d.diagnostics.length > 0 && (
                                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Diagnostik: {d.diagnostics.join(', ')}</p>
                              )}
                              {d.treatment && d.treatment.length > 0 && (
                                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Behandlung: {d.treatment.join(', ')}</p>
                              )}
                              {d.medications && d.medications.length > 0 && (
                                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Medikamente: {d.medications.join(', ')}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {hook.manualNotes && (
                        <div className="session-detail-section">
                          <h4>Ergänzungen</h4>
                          <p>{hook.manualNotes}</p>
                        </div>
                      )}

                      {hook.discussionPoints && (
                        <div className="session-detail-section">
                          <h4>Diskussionspunkte</h4>
                          <p className="community-discussion-text">{hook.discussionPoints}</p>
                        </div>
                      )}

                      {/* ── Diskussionsthread ── */}
                      {(() => {
                        const comments = getDiscussions(hook.id, hook.isMock);
                        return (
                          <div className="discussion-thread" onClick={e => e.stopPropagation()}>
                            <div className="discussion-header">
                              <h4 className="discussion-title">Diskussion</h4>
                              {comments.length > 0 && (
                                <span className="discussion-count">{comments.length}</span>
                              )}
                            </div>

                            {comments.length > 0 && (
                              <div className="discussion-comments">
                                {comments.map(comment => {
                                  const userReaction = userReactions[comment.id];
                                  return (
                                    <div key={comment.id} className="discussion-comment">
                                      <div
                                        className="discussion-avatar"
                                        style={{ background: avatarColor(comment.initials) }}
                                      >
                                        {comment.initials}
                                      </div>
                                      <div className="discussion-comment-body">
                                        <div className="discussion-comment-header">
                                          <span className="discussion-comment-author">{comment.author}</span>
                                          <span className="discussion-comment-date">{formatDate(comment.postedAt)}</span>
                                        </div>
                                        <p className="discussion-comment-text">{comment.text}</p>
                                        <div className="discussion-reactions">
                                          {REACTION_TYPES.map(r => {
                                            const baseCount = comment.reactions?.[r.key] || 0;
                                            const isActive = userReaction === r.key;
                                            const count = baseCount + (isActive ? 1 : 0);
                                            return (
                                              <button
                                                key={r.key}
                                                className={`reaction-btn${isActive ? ' active' : ''}`}
                                                style={isActive ? { '--rc': r.color, '--rb': r.activeBg } : {}}
                                                onClick={e => { e.stopPropagation(); toggleReaction(comment.id, r.key); }}
                                                title={r.label}
                                              >
                                                <span className="reaction-icon">{r.icon}</span>
                                                <span className="reaction-label">{r.label}</span>
                                                {count > 0 && <span className="reaction-count">{count}</span>}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <div className="discussion-input-area">
                              <div className="discussion-avatar own" style={{ background: '#0ea5e9' }}>Du</div>
                              <div className="discussion-input-wrap">
                                <textarea
                                  className="discussion-input"
                                  placeholder="Kommentar hinzufügen…"
                                  value={commentText}
                                  onChange={e => setCommentText(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  rows={2}
                                />
                                <button
                                  className="discussion-submit-btn"
                                  disabled={!commentText.trim()}
                                  onClick={e => {
                                    e.stopPropagation();
                                    addComment(hook.id, commentText.trim());
                                    setCommentText('');
                                  }}
                                >
                                  Senden
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {activeTab === 'mine' && !hook.isMock && (
                        <div className="community-hook-detail-actions">
                          <button
                            className="session-list-btn-delete"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(hook.id); }}
                          >
                            Entfernen
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="community-sidebar">
          <div className="community-widget">
            <h3 className="community-widget-title">Aktuelle Beiträge</h3>
            {recentHooks.map((hook) => (
              <div
                key={hook.id}
                className="community-widget-item"
                onClick={() => handleWidgetClick(hook)}
              >
                <span className="community-widget-item-name">{hook.name}</span>
                <div className="community-widget-item-meta">
                  <span>{hook.author}</span>
                  <span>{formatDateShort(hook.sharedAt)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="community-widget">
            <h3 className="community-widget-title">Meist gelesene Beiträge</h3>
            {mostReadHooks.map((hook) => (
              <div
                key={hook.id}
                className="community-widget-item"
                onClick={() => handleWidgetClick(hook)}
              >
                <span className="community-widget-item-name">{hook.name}</span>
                <div className="community-widget-item-meta">
                  <span>{hook.author}</span>
                  <span className="community-widget-views">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    {hook.views}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {deleteConfirmId && (
        <div className="session-modal-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="session-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Beitrag entfernen</h3>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
              Möchten Sie diesen Beitrag wirklich aus dem Netzwerk entfernen?
            </p>
            <div className="session-modal-actions">
              <button className="modal-btn-cancel" onClick={() => setDeleteConfirmId(null)}>
                Abbrechen
              </button>
              <button className="modal-btn-confirm" onClick={confirmDelete} style={{ background: '#dc2626' }}>
                Entfernen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommunityPage;
